import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/hooks/useAuth";
import { CartProvider } from "@/contexts/CartContext";
import { SiteAssetsProvider } from "@/contexts/SiteAssetsContext";
import ScrollManager from "@/components/ScrollManager";
import CartReplacementDialog from "@/components/CartReplacementDialog";
import FloatingCartButton from "@/components/FloatingCartButton";
import ReferralTracker from "@/components/ReferralTracker";
import FacebookPixel from "@/components/FacebookPixel";
import GoogleAnalytics from "@/components/GoogleAnalytics";
import PageAnalytics from "@/components/PageAnalytics";
import AnalyticsProvider from "@/components/AnalyticsProvider";
import ErrorBoundary from "@/components/ErrorBoundary";
// ChatWidget lazy-loaded to reduce initial bundle
const ChatWidget = lazy(() => import("@/components/chat/ChatWidget").then(m => ({ default: m.ChatWidget })));

// Critical pages loaded immediately
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

// Lazy load non-critical pages for code splitting
const Auth = lazy(() => import("./pages/Auth"));
const AdminAuth = lazy(() => import("./pages/AdminAuth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Admin = lazy(() => import("./pages/Admin"));
const Dashboard = lazy(() => import("./pages/admin/Dashboard"));
const Products = lazy(() => import("./pages/admin/Products"));
const FileLibrary = lazy(() => import("./pages/admin/FileLibrary"));
const Customers = lazy(() => import("./pages/admin/Customers"));
const Subscribers = lazy(() => import("./pages/admin/Subscribers"));
const Orders = lazy(() => import("./pages/admin/Orders"));
const Categories = lazy(() => import("./pages/admin/Categories"));
const Analytics = lazy(() => import("./pages/admin/Analytics"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));
const MyPurchases = lazy(() => import("./pages/MyPurchases"));
const Account = lazy(() => import("./pages/Account"));
const Templates = lazy(() => import("./pages/Templates"));
const CategoryTemplates = lazy(() => import("./pages/CategoryTemplates"));
const TemplateDetail = lazy(() => import("./pages/TemplateDetail"));
const Cart = lazy(() => import("./pages/Cart"));
const CallbackRequests = lazy(() => import("./pages/admin/CallbackRequests"));
const PromoCodes = lazy(() => import("./pages/admin/PromoCodes"));
const Affiliates = lazy(() => import("./pages/admin/Affiliates"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const AccessPurchases = lazy(() => import("./pages/AccessPurchases"));
const RequestAccess = lazy(() => import("./pages/RequestAccess"));
const AffiliateDashboard = lazy(() => import("./pages/AffiliateDashboard"));
const AffiliateApply = lazy(() => import("./pages/AffiliateApply"));
const AffiliateTerms = lazy(() => import("./pages/AffiliateTerms"));
const AffiliateMessages = lazy(() => import("./pages/AffiliateMessages"));
const AffiliateResources = lazy(() => import("./pages/AffiliateResources"));
const PacketReviewDashboard = lazy(() => import("./pages/admin/PacketReviewDashboard"));
const PacketReviewCaseAdmin = lazy(() => import("./pages/admin/PacketReviewCase"));
const SecurityEvents = lazy(() => import("./pages/admin/SecurityEvents"));
const AdminReviews = lazy(() => import("./pages/admin/Reviews"));
const AbandonedCarts = lazy(() => import("./pages/admin/AbandonedCarts"));
const MarketingIntegrations = lazy(() => import("./pages/admin/MarketingIntegrations"));
const ImageOptimization = lazy(() => import("./pages/admin/ImageOptimization"));
const SiteAssets = lazy(() => import("./pages/admin/SiteAssets"));
const Contact = lazy(() => import("./pages/Contact"));
const ContactSubmissions = lazy(() => import("./pages/admin/ContactSubmissions"));
const EmailLogs = lazy(() => import("./pages/admin/EmailLogs"));
const UserManagement = lazy(() => import("./pages/admin/UserManagement"));
const EmailTemplates = lazy(() => import("./pages/admin/EmailTemplates"));
const ChatbotKnowledge = lazy(() => import("./pages/admin/ChatbotKnowledge"));
const AllUsers = lazy(() => import("./pages/admin/AllUsers"));
const AdminMessages = lazy(() => import("./pages/admin/Messages"));
const AffiliateResourcesAdmin = lazy(() => import("./pages/admin/AffiliateResourcesAdmin"));
const AffiliateCodesAdmin = lazy(() => import("./pages/admin/AffiliateCodes"));
const TrackingPixels = lazy(() => import("./pages/admin/TrackingPixels"));
const PaymentGateways = lazy(() => import("./pages/admin/PaymentGateways"));
const ExportData = lazy(() => import("./pages/admin/ExportData"));
const BundleDetail = lazy(() => import("./pages/BundleDetail"));
const RecoverCart = lazy(() => import("./pages/RecoverCart"));
const RestoreCart = lazy(() => import("./pages/RestoreCart"));
const DownloadAnalytics = lazy(() => import("./pages/admin/DownloadAnalytics"));
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data for 5 minutes before considering it stale
      staleTime: 5 * 60 * 1000,
      // Keep unused data in cache for 30 minutes
      gcTime: 30 * 60 * 1000,
      // Retry failed requests up to 3 times with exponential backoff
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Don't refetch on window focus for better performance
      refetchOnWindowFocus: false,
      // Refetch on reconnect to ensure fresh data
      refetchOnReconnect: true,
    },
    mutations: {
      // Retry mutations once on failure
      retry: 1,
    },
  },
});

// Loading fallback for lazy-loaded routes
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <HelmetProvider>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} forcedTheme="light">
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <AuthProvider>
            <SiteAssetsProvider>
            <CartProvider>
            <Toaster />
            <Sonner />
            <CartReplacementDialog />
            <BrowserRouter>
              <FacebookPixel />
              <GoogleAnalytics />
              <PageAnalytics />
              <AnalyticsProvider />
              <ScrollManager />
              <ReferralTracker />
              <FloatingCartButton />
              <ChatWidget />
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/templates" element={<Templates />} />
                  <Route path="/templates/:categorySlug" element={<CategoryTemplates />} />
                  <Route path="/templates/:categorySlug/:templateSlug" element={<TemplateDetail />} />
                  <Route path="/cart" element={<Cart />} />
                  <Route path="/bundle" element={<BundleDetail />} />
                  <Route path="/checkout/success" element={<CheckoutSuccess />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/admin/login" element={<AdminAuth />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  <Route path="/refund" element={<RefundPolicy />} />
                  <Route path="/my-purchases" element={<MyPurchases />} />
                  <Route path="/account" element={<Account />} />
                  <Route path="/access/:token" element={<AccessPurchases />} />
                  <Route path="/request-access" element={<RequestAccess />} />
                  <Route path="/recover-cart/:token" element={<RecoverCart />} />
                  <Route path="/cart/restore/:token" element={<RestoreCart />} />
                  <Route path="/affiliate" element={<AffiliateDashboard />} />
                  <Route path="/affiliate-dashboard" element={<AffiliateDashboard />} />
                  <Route path="/affiliate/apply" element={<AffiliateApply />} />
                  <Route path="/affiliate/terms" element={<AffiliateTerms />} />
                  <Route path="/affiliate/messages" element={<AffiliateMessages />} />
                  <Route path="/affiliate/resources" element={<AffiliateResources />} />
                  <Route path="/admin" element={<Admin />}>
                    <Route index element={<ErrorBoundary><Dashboard /></ErrorBoundary>} />
                    <Route path="products" element={<ErrorBoundary><Products /></ErrorBoundary>} />
                    <Route path="file-library" element={<ErrorBoundary><FileLibrary /></ErrorBoundary>} />
                    <Route path="customers" element={<ErrorBoundary><Customers /></ErrorBoundary>} />
                    <Route path="subscribers" element={<ErrorBoundary><Subscribers /></ErrorBoundary>} />
                    <Route path="orders" element={<ErrorBoundary><Orders /></ErrorBoundary>} />
                    <Route path="callbacks" element={<ErrorBoundary><CallbackRequests /></ErrorBoundary>} />
                    <Route path="promo-codes" element={<ErrorBoundary><PromoCodes /></ErrorBoundary>} />
                    <Route path="affiliates" element={<ErrorBoundary><Affiliates /></ErrorBoundary>} />
                    <Route path="affiliate-codes" element={<ErrorBoundary><AffiliateCodesAdmin /></ErrorBoundary>} />
                    <Route path="categories" element={<ErrorBoundary><Categories /></ErrorBoundary>} />
                    <Route path="analytics" element={<ErrorBoundary><Analytics /></ErrorBoundary>} />
                    <Route path="download-analytics" element={<ErrorBoundary><DownloadAnalytics /></ErrorBoundary>} />
                    <Route path="packet-reviews" element={<ErrorBoundary><PacketReviewDashboard /></ErrorBoundary>} />
                    <Route path="packet-reviews/:caseId" element={<ErrorBoundary><PacketReviewCaseAdmin /></ErrorBoundary>} />
                    <Route path="security" element={<ErrorBoundary><SecurityEvents /></ErrorBoundary>} />
                    <Route path="reviews" element={<ErrorBoundary><AdminReviews /></ErrorBoundary>} />
                    <Route path="abandoned-carts" element={<ErrorBoundary><AbandonedCarts /></ErrorBoundary>} />
                    <Route path="marketing-integrations" element={<ErrorBoundary><MarketingIntegrations /></ErrorBoundary>} />
                    <Route path="image-optimization" element={<ErrorBoundary><ImageOptimization /></ErrorBoundary>} />
                    <Route path="site-assets" element={<ErrorBoundary><SiteAssets /></ErrorBoundary>} />
                    <Route path="contact-submissions" element={<ErrorBoundary><ContactSubmissions /></ErrorBoundary>} />
                    <Route path="email-logs" element={<ErrorBoundary><EmailLogs /></ErrorBoundary>} />
                    <Route path="email-templates" element={<ErrorBoundary><EmailTemplates /></ErrorBoundary>} />
                    <Route path="users" element={<ErrorBoundary><UserManagement /></ErrorBoundary>} />
                    <Route path="all-users" element={<ErrorBoundary><AllUsers /></ErrorBoundary>} />
                    <Route path="chatbot-knowledge" element={<ErrorBoundary><ChatbotKnowledge /></ErrorBoundary>} />
                    <Route path="messages" element={<ErrorBoundary><AdminMessages /></ErrorBoundary>} />
                    <Route path="affiliate-resources" element={<ErrorBoundary><AffiliateResourcesAdmin /></ErrorBoundary>} />
                    <Route path="tracking-pixels" element={<ErrorBoundary><TrackingPixels /></ErrorBoundary>} />
                    <Route path="payment-gateways" element={<ErrorBoundary><PaymentGateways /></ErrorBoundary>} />
                    <Route path="export-data" element={<ErrorBoundary><ExportData /></ErrorBoundary>} />
                  </Route>
                  <Route path="/contact" element={<Contact />} />
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
            </CartProvider>
            </SiteAssetsProvider>
          </AuthProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </HelmetProvider>
);

export default App;
